// SUI 2048 游戏 SignalR 连接管理 - 基于 noMatchApi.js 的 SignalR 实现
import { HubConnectionBuilder, LogLevel, HubConnectionState } from '@microsoft/signalr';
import { getToken, removeToken } from '../utils/Auth';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';

// SUI 游戏 SignalR Hub URL
// const SUI_SIGNALR_HUB_URL = `https://localhost:7137/suigamehub`;
const SUI_SIGNALR_HUB_URL = `https://mulproject2-cxg8bcb5abhbgaf0.westus2-01.azurewebsites.net/suigamehub`;

// SUI 游戏 SignalR 连接管理类
class GameFiSignalRManager {
  constructor() {
    this.connection = null;
    this.connectionId = null;
    this.userId = null;
    this.gameId = null;
    this.connectionState = HubConnectionState.Disconnected;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 5000;
    this.authToken = null;
    this.heartbeatInterval = null;
    this.isAuthenticated = false;
    this.isConnected = false;

    // 事件监听器
    this.messageListeners = new Set();
    this.connectionListeners = new Set();
    this.errorListeners = new Set();
    this.gameStateListeners = new Set();
    this.scoreUpdateListeners = new Set();
    this.playerActionListeners = new Set();
  }

  // 初始化 SignalR 连接
  async initializeConnection(userId, gameId = null, authToken = null) {
    try {
      this.userId = userId;
      this.gameId = gameId;
      this.authToken = authToken;

      // 构建连接
      const connectionBuilder = new HubConnectionBuilder()
        .withUrl(SUI_SIGNALR_HUB_URL, {
          accessTokenFactory: () => getToken(), // 提供 JWT token
          withCredentials: false, // JWT 认证时通常设为 false
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        })
        .withHubProtocol(new MessagePackHubProtocol())
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            if (retryContext.previousRetryCount < 3) {
              return Math.random() * 10000;
            } else {
              return 30000;
            }
          }
        })
        .withServerTimeout(60000) // 增加服务器超时到60秒
        .withKeepAliveInterval(30000) // 保持30秒心跳间隔
        .configureLogging(LogLevel.Information)
        .build();

      this.connection = connectionBuilder;

      // 设置事件监听器
      this.setupEventListeners();

      // 启动连接
      await this.startConnection();

      return true;
    } catch (error) {
      console.error('SUI SignalR connection initialization failed:', error);
      this.notifyErrorListeners('连接初始化失败', error);
      return false;
    }
  }

  // 设置事件监听器
  setupEventListeners() {
    if (!this.connection) return;

    // 连接状态事件
    this.connection.onclose((error) => {
      this.connectionState = HubConnectionState.Disconnected;
      this.isAuthenticated = false;
      this.isConnected = false;
      this.stopHeartbeat();
      console.warn('SUI SignalR connection closed:', error);
      this.notifyConnectionListeners('disconnected', error);
    });

    this.connection.onreconnecting((error) => {
      this.connectionState = HubConnectionState.Reconnecting;
      this.isConnected = false;
      console.warn('SUI SignalR reconnecting:', error);
      this.notifyConnectionListeners('reconnecting', error);
    });

    this.connection.onreconnected((connectionId) => {
      this.connectionId = connectionId;
      this.connectionState = HubConnectionState.Connected;
      this.isConnected = true;
      this.reconnectAttempts = 0;
      console.log('SUI SignalR reconnected:', connectionId);
      this.notifyConnectionListeners('reconnected', connectionId);
      this.startHeartbeat();

      // 重新加入当前游戏
      // if (this.gameId) {
      //   this.joinGame(this.gameId);
      // }
    });

    // 游戏业务事件监听 - 根据服务器端方法更新
    // 服务器返回的是 JSON 字符串，需要解析后转发给消息监听器
    this.connection.on('confirm_random_tile_response', (response) => {
      try {
        const responseData = typeof response === 'string' ? JSON.parse(response) : response;
        this.notifyMessageListeners(responseData);
      } catch (error) {
        console.error('Error parsing confirm_random_tile_response:', error);
      }
    });

    this.connection.on('new_game_response', (response) => {
      try {
        const responseData = typeof response === 'string' ? JSON.parse(response) : response;
        this.notifyMessageListeners(responseData);
      } catch (error) {
        console.error('Error parsing new_game_response:', error);
      }
    });

    this.connection.on('add_random_tile_response', (response) => {
      try {
        const responseData = typeof response === 'string' ? JSON.parse(response) : response;
        this.notifyMessageListeners(responseData);
      } catch (error) {
        console.error('Error parsing add_random_tile_response:', error);
      }
    });

    this.connection.on('get_double_response', (response) => {
      try {
        const responseData = typeof response === 'string' ? JSON.parse(response) : response;
        this.notifyMessageListeners(responseData);
      } catch (error) {
        console.error('Error parsing get_double_response:', error);
      }
    });

    this.connection.on('move_response', (response) => {
      try {
        const responseData = typeof response === 'string' ? JSON.parse(response) : response;
        this.notifyMessageListeners(responseData);
      } catch (error) {
        console.error('Error parsing move_response:', error);
      }
    });

    // 通用消息监听器
    this.connection.on('ReceiveMessage', (message) => {
      this.validateAndProcessMessage(message);
    });

    // 传送方块通知监听器
    this.connection.on('ReceiveTeleportNotification', (teleportData) => {
      try {
        const data = typeof teleportData === 'string' ? JSON.parse(teleportData) : teleportData;
        console.log('🌀 收到传送方块通知:', data);
        this.notifyMessageListeners(data);
      } catch (error) {
        console.error('Error parsing teleport notification:', error);
      }
    });

    // 对手游戏结束通知监听器（注意：SignalR 方法名会转换为小写开头）
    console.log('🔧 [SignalR] 註冊 receiveGameOverNotification 監聽器');
    this.connection.on('receiveGameOverNotification', (gameOverData) => {
      console.log('🎉🎉🎉 [SignalR] ===== 收到 receiveGameOverNotification 事件 =====');
      try {
        console.log('📩 [SignalR] receiveGameOverNotification 原始數據:', gameOverData);
        console.log('📩 [SignalR] 數據類型:', typeof gameOverData);

        const data = typeof gameOverData === 'string' ? JSON.parse(gameOverData) : gameOverData;
        console.log('🏁 [SignalR] 解析後的對手遊戲結束通知:', data);
        console.log('🏁 [SignalR] 通知類型:', data.type);
        console.log('🏁 [SignalR] 通知數據:', data.data);

        this.notifyMessageListeners(data);
        console.log('✅ [SignalR] 已通知消息監聽器，監聽器數量:', this.messageListeners.length);
      } catch (error) {
        console.error('❌ [SignalR] Error parsing game over notification:', error);
        console.error('❌ [SignalR] 原始數據:', gameOverData);
      }
    });
    console.log('✅ [SignalR] receiveGameOverNotification 監聽器已註冊');

    this.connection.on('AuthenticationRequired', () => {
      this.isAuthenticated = false;
      this.notifyErrorListeners('需要重新认证', null);
    });

    this.connection.on('SecurityViolation', (reason) => {
      this.notifyErrorListeners('安全违规', { reason });
      this.disconnect();
    });
  }

  // 启动连接
  async startConnection() {
    try {
      if (this.connection.state === HubConnectionState.Disconnected) {
        await this.connection.start();
        this.connectionId = this.connection.connectionId;
        this.connectionState = this.connection.state;
        this.isConnected = true;

        console.log('SUI SignalR connected:', this.connectionId);
        this.notifyConnectionListeners('connected', this.connectionId);

        // 启动心跳
        this.startHeartbeat();

        // 用户认证
        await this.authenticateUser();

        return true;
      }
    } catch (error) {
      console.error('SUI SignalR connection failed:', error);
      this.notifyErrorListeners('连接失败', error);

      // 实现重连机制
      await this.handleReconnect();
      return false;
    }
  }

  // 用户认证
  async authenticateUser() {
    try {
      if (!this.userId) {
        throw new Error('User ID is required for authentication');
      }

      // 使用 JWT 认证时，token 已经在连接时自动发送
      if (this.connection && this.connection.state === HubConnectionState.Connected) {
        this.isAuthenticated = true;
        return;
      }

      // 如果后端仍需要额外的认证步骤
      try {
        const result = await this.connection.invoke('AuthenticateUser');

        if (result && result.success) {
          this.isAuthenticated = true;
          console.log('SUI User authenticated successfully');
        } else {
          this.isAuthenticated = false;
          console.warn('SUI Authentication failed:', result?.message || '认证失败');
        }
      } catch (authError) {
        // 如果 AuthenticateUser 方法不存在或不需要，这是正常的
        console.log('No additional authentication required, using JWT token');
        this.isAuthenticated = true;
      }

    } catch (error) {
      console.error('SUI Authentication failed:', error);
      this.isAuthenticated = false;
      this.notifyErrorListeners('用户认证失败', error);
    }
  }

  // 心跳机制 - 根据服务器端方法更新
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(async () => {
      try {
        if (this.connection.state === HubConnectionState.Connected) {
          await this.connection.invoke('heartbeat');
        }
      } catch (error) {
        console.warn('SUI Heartbeat failed:', error);
      }
    }, 30000); // 每30秒发送心跳
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // 重连机制
  async handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('SUI Max reconnection attempts reached');
      this.notifyErrorListeners('超过最大重连次数', null);
      return false;
    }

    this.reconnectAttempts++;
    console.log(`SUI Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

    await new Promise(resolve => setTimeout(resolve, this.reconnectDelay));

    try {
      await this.startConnection();
      return true;
    } catch (error) {
      console.error('SUI Reconnection failed:', error);
      return this.handleReconnect();
    }
  }

  // 消息验证和处理
  validateAndProcessMessage(message) {
    try {
      // 基本验证
      if (!message || !message.type) {
        console.warn('Invalid SUI message received:', message);
        return;
      }

      // 安全检查
      if (JSON.stringify(message).length > 10000) {
        console.warn('SUI Message too large, ignored:', message.id);
        return;
      }

      this.notifyMessageListeners(message);
    } catch (error) {
      console.error('SUI Message validation failed:', error);
      this.notifyErrorListeners('消息验证失败', error);
    }
  }

  // 等待连接就绪
  async waitForConnection(timeoutMs = 30000) { // 增加超时时间到30秒
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
      if (this.connection &&
        this.connection.state === HubConnectionState.Connected &&
        this.isAuthenticated) {
        return true;
      }

      if (this.connection && this.connection.state === HubConnectionState.Disconnected) {
        throw new Error('SUI Connection is disconnected');
      }

      // 等待一小段时间再检查
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    throw new Error(`SUI Connection timeout after ${timeoutMs}ms`);
  }

  // 检查连接状态
  isConnectionReady() {
    return this.connection &&
      this.connection.state === HubConnectionState.Connected &&
      this.isAuthenticated &&
      this.isConnected;
  }

  // 加入游戏
  async joinGame(gameId) {
    try {
      if (!this.isAuthenticated) {
        throw new Error('User not authenticated');
      }

      if (!this.isConnectionReady()) {
        console.log('SUI Connection not ready, waiting...');
        await this.waitForConnection();
      }

      const result = await this.connection.invoke('JoinGame', gameId);

      console.log('SUI joinGame result:', result);

      if (result.success) {
        this.gameId = gameId;
        console.log('Successfully joined SUI game:', gameId);
        return true;
      } else {
        throw new Error(result.message || '加入游戏失败');
      }
    } catch (error) {
      console.error('Failed to join SUI game:', error);
      this.notifyErrorListeners('加入游戏失败', error);
      return false;
    }
  }

  // 离开游戏
  async leaveGame(gameId) {
    try {
      if (!this.isAuthenticated) {
        throw new Error('User not authenticated');
      }

      if (!this.isConnectionReady()) {
        console.log('SUI Connection not ready, waiting...');
        await this.waitForConnection();
      }

      const result = await this.connection.invoke('LeaveGame', gameId, this.userId, Date.now());

      if (result.success) {
        if (this.gameId === gameId) {
          this.gameId = null;
        }
        console.log('Successfully left SUI game:', gameId);
        return true;
      } else {
        throw new Error(result.message || '离开游戏失败');
      }
    } catch (error) {
      console.error('Failed to leave SUI game:', error);
      this.notifyErrorListeners('离开游戏失败', error);
      return false;
    }
  }

  // 发送游戏动作 - 根据服务器端方法更新
  async sendGameAction(action) {
    try {
      if (!this.isAuthenticated) {
        throw new Error('User not authenticated');
      }

      if (!action || !action.type) {
        throw new Error('Game action is required');
      }

      if (!this.isConnectionReady()) {
        console.log('SUI Connection not ready, waiting...');
        await this.waitForConnection();
      }

      let result;

      // 根据动作类型调用相应的服务器方法，增加超时处理
      const invokeWithTimeout = async (methodName, ...args) => {
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`Server timeout elapsed without receiving a message from the server for ${methodName}`)), 30000);
        });

        const invokePromise = this.connection.invoke(methodName, ...args);

        return Promise.race([invokePromise, timeoutPromise]);
      };

      switch (action.type) {
        case 'confirm_random_tile':
          result = await invokeWithTimeout('confirm_random_tile', action.gameId, action.row, action.col);
          break;

        case 'new_game':
          result = await invokeWithTimeout('new_game',
            action.gameType,
            action.multiplayerGameId,
            action.userSignalRConnection);
          break;

        case 'add_random_tile':
          result = await invokeWithTimeout('add_random_tile', action.gameId);
          break;

        case 'get_double':
          result = await invokeWithTimeout('get_double');
          break;

        case 'move':
          result = await invokeWithTimeout('move', action.gameId, action.direction, action.multiplayerGameId);
          break;

        case 'send_sign_data':
          result = await invokeWithTimeout('send_sign_data', action.gameId, action.signType, action.userSignBytes, action.sponsorSign, action.userSign);
          break;

        case 'heartbeat':
          result = await invokeWithTimeout('heartbeat');
          break;

        case 'get_gameObjectId':
          result = await invokeWithTimeout('get_gameObjectId', action.gameId);
          break;

        case 'get_game_state':
          result = await invokeWithTimeout('get_game_state', action.gameId);
          break;

        case 'getNotSignEvents':
          result = await invokeWithTimeout('getNotSignEvents', action.gameId);
          break;

        case 'get_teleport_tiles':
          result = await invokeWithTimeout('get_teleport_tiles', action.gameId);
          break;

        case 'activate_teleport_tile':
          result = await invokeWithTimeout('activate_teleport_tile', action.gameId, action.row, action.col);
          break;

        default:
          throw new Error(`Unknown action type: ${action.type}`);
      }

      // 直接返回服务器响应，不再通过事件发送
      // console.log('SUI Game action response:', action.type, result);

      // 如果结果是字符串，尝试解析为 JSON
      if (typeof result === 'string') {
        try {
          return JSON.parse(result);
        } catch (error) {
          console.error('Failed to parse response JSON:', error);
          return { type: 'error', message: 'Failed to parse server response' };
        }
      }

      return result;

    } catch (error) {
      console.error('Failed to send SUI game action:', error);

      // 检查是否是超时错误
      if (error.message.includes('Server timeout elapsed without receiving a message from the server')) {
        console.warn('⚠️ 服务器响应超时，尝试重新连接...');
        this.notifyErrorListeners('服务器响应超时，正在尝试重新连接', error);

        // 尝试重新连接
        try {
          await this.handleReconnect();
          // 重新连接成功后，可以选择重试操作或返回错误
          throw new Error('服务器响应超时，请稍后重试');
        } catch (reconnectError) {
          console.error('重连失败:', reconnectError);
          throw new Error('服务器响应超时且重连失败，请检查网络连接');
        }
      }

      this.notifyErrorListeners('发送游戏动作失败', error);
      throw error;
    }
  }

  // 等待服务器响应 - 现在方法直接返回，不需要等待事件
  async waitForServerResponse(actionType, timeout = 5000) {
    console.log(`🔄 注意: waitForServerResponse 已过时，现在方法直接返回响应: ${actionType}`);

    // 返回一个模拟的响应，表示方法已直接返回
    return {
      type: `${actionType}_response`,
      message: 'Method now returns directly, no need to wait for events'
    };
  }

  // 断开连接
  async disconnect() {
    try {
      this.stopHeartbeat();

      if (this.connection && this.connection.state === HubConnectionState.Connected) {
        await this.connection.stop();
      }

      this.connectionState = HubConnectionState.Disconnected;
      this.isAuthenticated = false;
      this.isConnected = false;
      this.gameId = null;
      this.connectionId = null;

      console.log('SUI SignalR disconnected');
    } catch (error) {
      console.error('SUI Disconnect failed:', error);
    }
  }

  // 事件监听器管理
  onMessage(callback) {
    this.messageListeners.add(callback);
    return () => this.messageListeners.delete(callback);
  }

  onConnection(callback) {
    this.connectionListeners.add(callback);
    return () => this.connectionListeners.delete(callback);
  }

  onError(callback) {
    this.errorListeners.add(callback);
    return () => this.errorListeners.delete(callback);
  }

  onGameState(callback) {
    this.gameStateListeners.add(callback);
    return () => this.gameStateListeners.delete(callback);
  }

  onScoreUpdate(callback) {
    this.scoreUpdateListeners.add(callback);
    return () => this.scoreUpdateListeners.delete(callback);
  }

  onPlayerAction(callback) {
    this.playerActionListeners.add(callback);
    return () => this.playerActionListeners.delete(callback);
  }

  // 通知监听器
  notifyMessageListeners(message) {
    this.messageListeners.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('SUI Message listener error:', error);
      }
    });
  }

  notifyConnectionListeners(status, data) {
    this.connectionListeners.forEach(callback => {
      try {
        callback(status, data);
      } catch (error) {
        console.error('SUI Connection listener error:', error);
      }
    });
  }

  notifyErrorListeners(message, error) {
    this.errorListeners.forEach(callback => {
      try {
        callback(message, error);
      } catch (error) {
        console.error('SUI Error listener error:', error);
      }
    });
  }

  notifyGameStateListeners(gameState) {
    this.gameStateListeners.forEach(callback => {
      try {
        callback(gameState);
      } catch (error) {
        console.error('SUI Game state listener error:', error);
      }
    });
  }

  notifyScoreUpdateListeners(scoreData) {
    this.scoreUpdateListeners.forEach(callback => {
      try {
        callback(scoreData);
      } catch (error) {
        console.error('SUI Score update listener error:', error);
      }
    });
  }

  notifyPlayerActionListeners(action) {
    this.playerActionListeners.forEach(callback => {
      try {
        callback(action);
      } catch (error) {
        console.error('SUI Player action listener error:', error);
      }
    });
  }

  // 获取连接状态
  getConnectionState() {
    return {
      state: this.connectionState,
      isAuthenticated: this.isAuthenticated,
      isConnected: this.isConnected,
      connectionId: this.connectionId,
      gameId: this.gameId,
      userId: this.userId
    };
  }

  // 传送方块相关方法
  async getTeleportTiles(gameId) {
    try {
      const result = await this.sendGameAction({
        type: 'get_teleport_tiles',
        gameId: gameId
      });

      console.log('🌀 获取传送方块结果:', result);
      return result;
    } catch (error) {
      console.error('获取传送方块失败:', error);
      throw error;
    }
  }

  async activateTeleportTile(gameId, row, col) {
    try {
      const result = await this.sendGameAction({
        type: 'activate_teleport_tile',
        gameId: gameId,
        row: row,
        col: col
      });

      console.log('🌀 激活传送方块结果:', result);
      return result;
    } catch (error) {
      console.error('激活传送方块失败:', error);
      throw error;
    }
  }
}

// 创建全局 SUI SignalR 管理器实例
const suiSignalRManager = new GameFiSignalRManager();

// SUI SignalR 相关 API
export const initializeSuiSignalR = async (userId, gameId = null, authToken = null) => {
  return await suiSignalRManager.initializeConnection(userId, gameId, authToken);
};

export const disconnectSuiSignalR = async () => {
  return await suiSignalRManager.disconnect();
};

export const joinSuiGame = async (gameId) => {
  return await suiSignalRManager.joinGame(gameId);
};

export const leaveSuiGame = async (gameId) => {
  return await suiSignalRManager.leaveGame(gameId);
};

export const sendSuiGameAction = async (action) => {
  return await suiSignalRManager.sendGameAction(action);
};

export const waitForSuiServerResponse = async (actionType, timeout = 5000) => {
  return await suiSignalRManager.waitForServerResponse(actionType, timeout);
};

export const getSuiSignalRConnectionState = () => {
  return suiSignalRManager.getConnectionState();
};

// 事件监听器
export const onSuiMessage = (callback) => {
  return suiSignalRManager.onMessage(callback);
};

export const onSuiConnection = (callback) => {
  return suiSignalRManager.onConnection(callback);
};

export const onSuiError = (callback) => {
  return suiSignalRManager.onError(callback);
};

export const onSuiGameState = (callback) => {
  return suiSignalRManager.onGameState(callback);
};

export const onSuiScoreUpdate = (callback) => {
  return suiSignalRManager.onScoreUpdate(callback);
};

export const onSuiPlayerAction = (callback) => {
  return suiSignalRManager.onPlayerAction(callback);
};

// 传送方块相关 API
export const getSuiTeleportTiles = async (gameId) => {
  return await suiSignalRManager.getTeleportTiles(gameId);
};

export const activateSuiTeleportTile = async (gameId, row, col) => {
  return await suiSignalRManager.activateTeleportTile(gameId, row, col);
};

// 导出 SUI SignalR 管理器实例（用于高级用法）
export { suiSignalRManager };
